/**
 * Optimizer (/optimizer) — a query builder in Floodlight (v1.71: the original
 * glass look with purple/blue accents and Floodlight type). Every filter
 * becomes plain-English search text: Straight bets go to /optimizer/results as
 * one or more queries; a same-game parlay names its game once and stacks up to
 * three legs. The form logic is hooks/useOptimizerForm.js (shared with the
 * Classic screen); the filter rules live in utils/optimizerFilters.js (tested).
 */
import React from 'react';
import { View, Text, Pressable, TextInput, Modal, ActivityIndicator, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AutocompleteDropdown from '../components/AutocompleteDropdown';
import { FLPage, FLHeadline, FLButton, FLKicker, FL, gutter } from '../components/floodlight';
import { sportsData, TIME_FILTERS } from '../data/sportsData';
import { MAX_SGP_LEGS, playerEmptyText } from '../utils/optimizerFilters';
import { matchPlayerNames } from '../utils/playerNameSearch';
import { ALL_SPORTSBOOKS, POPULAR_SPORTSBOOKS, BOOST_OPTIONS } from '../data/optimizerBooks';
import useOptimizerForm from '../hooks/useOptimizerForm';
import { styles } from './sameGameParlayStyles';

const TIERS = [[1, 'Most popular'], [2, 'US legal'], [3, 'Offshore / sharp'], [4, 'DFS / props']];

const SameGameParlayScreen = () => {
    const {
        navigateTo, canAddBackgroundQuery, backgroundQueryMode,
        loading, errorMsg, notice, betType, sgp, switchMode, showComingSoon,
        selectedSport, selectedLeague, selectedTeam, selectedPlayer, selectedMarket, selectedMarketCategory,
        selectedSportsbooks, setSelectedSportsbooks, selectedTimeFilter, setSelectedTimeFilter, selectedFixture,
        lineValue, setLineValue, lineDirection, setLineDirection, boostPercent,
        showSportsbooksDropdown, setShowSportsbooksDropdown,
        loadingCacheData, cacheError, leagueFixtures, loadingFixtures, playerOptions, loadingPlayers,
        leagues, games, teams, markets, categories, lineIssue,
        handleSportSelect, handleLeagueSelect, handleTeamSelect, handleMarketSelect, handlePlayerSelect, handleFixtureSelect, handleCategorySelect,
        queries, legs, addQuery, addLeg, removeQuery, removeLeg, clearAllFilters,
        finalQuery, runSearch, toggleSportsbook, myBookNames,
    } = useOptimizerForm();

    const { width } = useWindowDimensions();
    const pad = gutter(width);
    const compact = width < 768;
    const wide = width >= 960;

    const chip = (name, keyPrefix = '') => {
        const on = selectedSportsbooks.includes(name);
        return (
            <Pressable
                key={`${keyPrefix}${name}`}
                onPress={() => toggleSportsbook(name)}
                accessibilityRole="checkbox"
                aria-checked={!!on}
                dataSet={{ flChip: on ? 'on' : '' }}
                style={[styles.chip, on && styles.chipOn]}
            >
                <Text style={[styles.chipText, on && styles.chipTextOn]}>{name}</Text>
            </Pressable>
        );
    };

    const field = { tone: 'paper', minInputWidth: 0 };
    const colHead = (n, title) => (
        <View style={styles.colHead}>
            <Text style={styles.colIndex}>{n}</Text>
            <Text style={styles.colTitle}>{title}</Text>
        </View>
    );

    const listRows = sgp ? legs : queries;

    return (
        <FLPage
            overlay={showComingSoon && (
                <Modal visible transparent animationType="fade">
                    <View style={styles.soonBackdrop}>
                        <View style={styles.soonCard} accessibilityViewIsModal>
                            <FLKicker>Optimizer</FLKicker>
                            <FLHeadline lines={['Coming', 'soon.']} accentLine={1} size={compact ? 40 : 56} level={2} />
                            <Text style={styles.soonBody}>The Bet Optimizer is in beta. Stay tuned for the official launch.</Text>
                            <FLButton label="Go home" variant="green" onPress={() => navigateTo('Home')} size="lg" />
                        </View>
                    </View>
                </Modal>
            )}
        >
            <View style={[styles.wrap, { paddingHorizontal: pad }]}>
                {/* Hero */}
                <View style={[styles.hero, !wide && styles.heroStacked]}>
                    <View style={styles.heroText}>
                        <FLKicker index={1}>Optimizer</FLKicker>
                        <FLHeadline
                            key={betType}
                            lines={sgp ? ['Build the', 'parlay.'] : ['Find the', 'best number.']}
                            accentLine={1}
                            size={compact ? 38 : wide ? 72 : 56}
                            level={1}
                            style={{ marginTop: 18 }}
                        />
                        <Text style={styles.lede}>
                            {sgp
                                ? `Pick a game, stack up to ${MAX_SGP_LEGS} legs, and we price the combination at every book that offers it.`
                                : 'Narrow by sport, team, player and market. We compare every book and rank by expected value.'}
                        </Text>
                    </View>
                    <View style={[styles.heroSide, !wide && styles.heroSideStacked]}>
                        <View style={[styles.modes, compact && styles.modesStacked]} accessibilityRole="tablist" accessibilityLabel="Search type">
                            {[['Straight', 'Straight bets'], ['SGP', 'Same-game parlay']].map(([value, text], i) => {
                                const on = betType === value;
                                return (
                                    <Pressable
                                        key={value}
                                        onPress={() => switchMode(value)}
                                        accessibilityRole="tab"
                                        aria-selected={!!on}
                                        dataSet={{ flChip: on ? 'on' : '' }}
                                        style={[styles.mode, compact && styles.modeStacked, on && styles.modeOn, i > 0 && styles.modeDivider]}
                                    >
                                        <Text style={[styles.modeText, on && styles.modeTextOn]}>{text}</Text>
                                    </Pressable>
                                );
                            })}
                        </View>
                        <View style={styles.status} accessibilityLiveRegion="polite">
                            {loadingCacheData ? (
                                <>
                                    <ActivityIndicator size="small" color={FL.ink} />
                                    <Text style={styles.statusText}>Loading today’s games</Text>
                                </>
                            ) : cacheError ? (
                                <>
                                    <Ionicons name="cloud-offline-outline" size={14} color={FL.amber} />
                                    <Text style={[styles.statusText, { color: FL.amber }]}>Live game list unavailable, using the built-in list</Text>
                                </>
                            ) : leagueFixtures.length > 0 ? (
                                <>
                                    <View style={styles.statusDot} />
                                    <Text style={styles.statusText}>{leagueFixtures.length} {selectedLeague} games loaded</Text>
                                </>
                            ) : null}
                        </View>
                    </View>
                </View>

                {/* Form: who / what */}
                <View style={[styles.grid, !wide && styles.gridStacked, { zIndex: 1000 }]}>
                    <View style={[styles.col, wide ? styles.colLeft : styles.colStackedTop, { zIndex: 500 }]}>
                        {colHead('A', sgp ? 'The game' : 'Who')}
                        <AutocompleteDropdown {...field} label="Sport" value={selectedSport} placeholder="Any sport" options={Object.keys(sportsData).sort()} onValueChange={handleSportSelect} zIndex={300} />
                        <AutocompleteDropdown {...field} label="League" value={selectedLeague} placeholder={`${leagues.length} leagues`} options={leagues} onValueChange={handleLeagueSelect} zIndex={290} />
                        {sgp && (
                            <AutocompleteDropdown
                                {...field}
                                label="Game"
                                value={selectedFixture?.label || ''}
                                placeholder={loadingFixtures ? 'Loading games…' : selectedLeague ? `${games.length} games` : 'Pick a league first'}
                                options={games.map((g) => g.label)}
                                onValueChange={handleFixtureSelect}
                                zIndex={280}
                            />
                        )}
                        <AutocompleteDropdown {...field} label="Team" value={selectedTeam} placeholder={loadingFixtures ? 'Loading teams…' : `${teams.length} teams`} options={teams} onValueChange={handleTeamSelect} zIndex={270} />
                        <AutocompleteDropdown
                            {...field}
                            label={loadingPlayers ? 'Player · loading' : playerOptions.length ? `Player · ${playerOptions.length}` : 'Player'}
                            value={selectedPlayer}
                            placeholder="Type or pick a player"
                            options={playerOptions}
                            onValueChange={handlePlayerSelect}
                            zIndex={265}
                            allowFreeText
                            emptyText={playerEmptyText(selectedTeam)}
                            matchOptions={matchPlayerNames}
                        />
                    </View>

                    <View style={[styles.col, wide ? styles.colRight : styles.colStackedBottom, { zIndex: 400 }]}>
                        {colHead('B', 'The bet')}
                        <AutocompleteDropdown
                            {...field}
                            label="Market category"
                            value={selectedMarketCategory}
                            placeholder="All categories"
                            options={categories}
                            onValueChange={handleCategorySelect}
                            zIndex={260}
                        />
                        <AutocompleteDropdown {...field} label="Market" value={selectedMarket} placeholder={`${markets.length} markets`} options={markets} onValueChange={handleMarketSelect} zIndex={250} />
                        <View style={{ zIndex: 240 }}>
                            <View style={styles.lineRow}>
                                <View style={styles.lineCell}>
                                    <Text style={styles.fieldLabel}>Line</Text>
                                    <View style={[styles.textField, !!lineIssue && { borderColor: FL.red }]} dataSet={{ flField: '' }}>
                                        <TextInput
                                            placeholder="e.g. 25.5"
                                            placeholderTextColor={FL.muted}
                                            style={styles.textInput}
                                            value={lineValue}
                                            onChangeText={(t) => setLineValue(t.replace(/[^0-9.+-]/g, ''))}
                                            keyboardType="numbers-and-punctuation"
                                            accessibilityLabel="Line"
                                            aria-invalid={!!lineIssue || undefined}
                                        />
                                    </View>
                                </View>
                                <View style={styles.lineCell}>
                                    <AutocompleteDropdown {...field} label="Over / under" value={lineDirection} placeholder="Either" options={['Over', 'Under']} onValueChange={setLineDirection} zIndex={240} />
                                </View>
                            </View>
                            {!!lineIssue && <Text style={styles.hint} accessibilityLiveRegion="polite">{lineIssue}</Text>}
                        </View>
                        {!sgp && (
                            <AutocompleteDropdown
                                {...field}
                                label="When"
                                value={selectedTimeFilter ? TIME_FILTERS.find((t) => t.value === selectedTimeFilter)?.label || '' : ''}
                                placeholder="All games"
                                options={TIME_FILTERS.map((t) => t.label)}
                                onValueChange={(text) => setSelectedTimeFilter(TIME_FILTERS.find((t) => t.label === text)?.value || null)}
                                zIndex={230}
                            />
                        )}
                    </View>
                </View>

                {/* Books */}
                <View style={[styles.books, compact && styles.booksStacked, { zIndex: 100 }]}>
                    <View style={[styles.boost, compact && styles.boostStacked]}>
                        <View style={[styles.colHead, { marginBottom: 8, alignItems: 'center' }]}>
                            <Text style={[styles.fieldLabel, { marginBottom: 0 }]}>Profit boost</Text>
                            <View style={styles.betaTag}><Text style={styles.betaText}>Beta</Text></View>
                        </View>
                        <AutocompleteDropdown {...field} label="" value={BOOST_OPTIONS.find((b) => b.value === boostPercent)?.label || 'No Boost'} placeholder="No Boost" options={BOOST_OPTIONS.map((b) => b.label)} onValueChange={() => {}} zIndex={90} disabled />
                    </View>
                    <View style={styles.bookPicker}>
                        <Text style={styles.fieldLabel}>Sportsbooks · {selectedSportsbooks.length ? `${selectedSportsbooks.length} picked` : 'all'}</Text>
                        <Pressable
                            style={styles.selector}
                            onPress={() => setShowSportsbooksDropdown(!showSportsbooksDropdown)}
                            accessibilityRole="button"
                            aria-expanded={!!showSportsbooksDropdown}
                            dataSet={{ flField: '' }}
                        >
                            <Text style={styles.selectorText} numberOfLines={1}>
                                {selectedSportsbooks.length === 0
                                    ? 'Every sportsbook (tap to filter)'
                                    : selectedSportsbooks.slice(0, 3).join(', ') + (selectedSportsbooks.length > 3 ? ` +${selectedSportsbooks.length - 3} more` : '')}
                            </Text>
                            <Ionicons name={showSportsbooksDropdown ? 'chevron-up' : 'chevron-down'} size={18} color={FL.ink} />
                        </Pressable>
                        {showSportsbooksDropdown && (
                            <View style={styles.panel}>
                                <View style={styles.panelActions}>
                                    {myBookNames.length > 0 && <FLButton label="My books" variant="outline" size="sm" arrow={false} icon="star-outline" onPress={() => setSelectedSportsbooks(myBookNames)} />}
                                    <FLButton label="Popular" variant="outline" size="sm" arrow={false} onPress={() => setSelectedSportsbooks(POPULAR_SPORTSBOOKS)} />
                                    <FLButton label="Clear" variant="outline" size="sm" arrow={false} onPress={() => setSelectedSportsbooks([])} />
                                    <FLButton label="Done" variant="green" size="sm" arrow={false} icon="checkmark" onPress={() => setShowSportsbooksDropdown(false)} />
                                </View>
                                {myBookNames.length > 0 && (
                                    <>
                                        <View style={styles.tierHead}><View style={styles.tierRule} /><Text style={styles.tierLabel}>My books</Text></View>
                                        <View style={styles.chips}>{myBookNames.map((n) => chip(n, 'my_'))}</View>
                                    </>
                                )}
                                {TIERS.map(([tier, title]) => (
                                    <React.Fragment key={tier}>
                                        <View style={styles.tierHead}><View style={styles.tierRule} /><Text style={styles.tierLabel}>{title}</Text></View>
                                        <View style={styles.chips}>{ALL_SPORTSBOOKS.filter((b) => b.tier === tier).map((b) => chip(b.name))}</View>
                                    </React.Fragment>
                                ))}
                            </View>
                        )}
                        {!showSportsbooksDropdown && selectedSportsbooks.length > 0 && (
                            <View style={styles.picked}>{selectedSportsbooks.map((n) => chip(n, 'picked_'))}</View>
                        )}
                    </View>
                </View>

                {/* Added queries / legs */}
                {listRows.length > 0 && (
                    <View style={styles.list}>
                        <View style={styles.listHead}>
                            <FLKicker index={2}>{sgp ? `Parlay · ${legs.length} of ${MAX_SGP_LEGS} legs` : `Combined · ${queries.length}`}</FLKicker>
                            {sgp && legs[0] && <Text style={styles.rowMeta}>{legs[0].fixture.label}</Text>}
                        </View>
                        {listRows.map((item, i) => (
                            <View key={item.id} style={[styles.row, i === listRows.length - 1 && styles.rowLast]} dataSet={{ flRow: 'static' }}>
                                <Text style={styles.rowNum} dataSet={{ flRowNum: '' }}>{sgp ? 'Leg' : 'Q'} {String(i + 1).padStart(2, '0')}</Text>
                                <View style={styles.rowBody} dataSet={{ flRowBody: '' }}>
                                    <Text style={styles.rowText}>{sgp ? item.description : item.text}</Text>
                                </View>
                                <Pressable
                                    style={styles.remove}
                                    onPress={() => (sgp ? removeLeg(item.id) : removeQuery(item.id))}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Remove ${sgp ? 'leg' : 'query'} ${i + 1}`}
                                    dataSet={{ flChip: '' }}
                                >
                                    <Ionicons name="close" size={18} color={FL.ink} />
                                </Pressable>
                            </View>
                        ))}
                    </View>
                )}
                {!!notice && <Text style={styles.notice} accessibilityLiveRegion="polite">{notice}</Text>}

                {/* Your search */}
                <View style={styles.stage} accessibilityLiveRegion="polite">
                    <View style={styles.stageHead}>
                        <View style={styles.stageDot} />
                        <Text style={styles.stageLabel}>Your search</Text>
                    </View>
                    <Text style={[styles.stageText, !finalQuery && styles.stageEmpty]} selectable>
                        {finalQuery || (sgp ? 'Pick a game and add at least one leg' : 'Pick a sport, team, player or market to start')}
                    </Text>
                </View>

                {!!errorMsg && (
                    <View style={styles.error} accessibilityLiveRegion="assertive">
                        <Text style={styles.errorText}>{errorMsg}</Text>
                    </View>
                )}

                {/* Actions */}
                <View style={[styles.actions, compact && styles.actionsStacked]}>
                    <FLButton
                        label={sgp ? 'Add leg' : 'Add query'}
                        variant="outline"
                        icon="add"
                        onPress={sgp ? addLeg : addQuery}
                        accessibilityLabel={sgp ? 'Add leg' : 'Add this as another query'}
                        disabled={sgp && legs.length >= MAX_SGP_LEGS}
                    />
                    <FLButton label="Clear all" variant="outline" icon="refresh" onPress={clearAllFilters} />
                    {canAddBackgroundQuery && !backgroundQueryMode && (
                        <FLButton
                            label="In background"
                            variant="outline"
                            icon="layers-outline"
                            onPress={() => runSearch(true)}
                            disabled={!finalQuery || loading}
                            accessibilityLabel="Run this search in the background"
                        />
                    )}
                    <FLButton
                        label={sgp ? 'Optimize parlay' : 'Search'}
                        variant="green"
                        size="lg"
                        icon={sgp ? 'flash' : 'search'}
                        busy={loading}
                        busyLabel="Searching…"
                        disabled={!finalQuery}
                        onPress={() => runSearch(false)}
                        accessibilityLabel={sgp ? 'Optimize this parlay' : 'Search'}
                        style={[styles.primary, compact && styles.primaryStacked]}
                    />
                </View>
            </View>
        </FLPage>
    );
};

export default SameGameParlayScreen;
